import type {
  AddUnsignedDocumentsPayload,
  AddUnsignedDocumentsResponse,
  AttachBankManagerPayload,
  AttachBankManagerResponse,
  CalculateCreditPayload,
  CalculateCreditResponse,
  CancelCreditResponse,
  CreditProgramsResponse,
  DocumentTypeArray,
  GetAnalyticsResponse,
  GetBankManagersPayload,
  GetBankTransferDetailsPayload,
  GetBankTransferDetailsResponse,
  GetCreditProgramResponse,
  GetUnsignedDocumentsPayload,
  ObtainCreditBankManagerPayload,
  ObtainCreditBankManagerResponse,
  PaginatedGetBankManagersResponse,
  Q19CallResponse,
  SetCardDeliveryAddressPayload,
  SetCardDeliveryAddressResponse,
  TransformedCreditProgramsResponse,
  TransformedGetCreditProgramResponse
} from 'share/types/api/credits'
import { isScoringOptionsArray, isScoringOptionsObject } from 'utils/type-guards'

import { api } from '.'

const authApi = api.injectEndpoints({
  endpoints: build => ({
    getCreditPrograms: build.query<TransformedCreditProgramsResponse, void>({
      query: () => ({
        url: '/credits/credit_programs/',
        method: 'get'
      }),
      transformResponse: (response: CreditProgramsResponse) => {
        return {
          data: response,
          minMonth: Math.min(...response.map(program => program.min_period)),
          maxMonth: Math.max(...response.map(program => program.max_period)),
          minAmount: Math.min(...response.map(program => program.min_loan_amount)),
          maxAmount: Math.max(...response.map(program => program.max_loan_amount))
        }
      }
    }),
    getCreditProgram: build.query<TransformedGetCreditProgramResponse, string>({
      query: id => ({
        url: `/credits/${id}/programs/`,
        method: 'get'
      }),
      transformResponse: (response: GetCreditProgramResponse) => {
        const transformedResponse = response
          .map(program => {
            const subPrograms = program.programs.filter(p => !(Object.keys(p.options).length === 0 && !p.with_pledges))
            return { ...program, programs: subPrograms }
          })
          .map(program => {
            const interestRates: Array<string> = []

            if (program.program_code === 'DAMU') {
              const firstProgram = program.programs.at(0)
              if (isScoringOptionsObject(firstProgram)) {
                interestRates.push(`${firstProgram.options?.credit_params?.interest_rate}%`)
              }
            } else if (program.program_code === 'OWN_PROGRAM') {
              program.programs.map(p => {
                if (isScoringOptionsObject(p)) {
                  const isDummyProgram = Object.keys(p.options).length === 0
                  if (isDummyProgram) return
                  interestRates.push(`${p.options.credit_params.interest_rate}%`)
                } else if (isScoringOptionsArray(p)) {
                  const firstOption = p.options.at(0)
                  if (!firstOption) return
                  interestRates.push(`${firstOption.credit_params.interest_rate}%`)
                }
              })
            }
            return {
              ...program,
              maxPeriod: Math.max(...program.programs.map(p => p.max_period)) / 12,
              maxLoanAmount: Math.max(...program.programs.map(p => p.max_loan_amount ?? 0)) / 1_000_000,
              interestRate: interestRates.join(' / ')
            }
          })

        return transformedResponse
      }
    }),
    calculateCredit: build.mutation<CalculateCreditResponse, CalculateCreditPayload>({
      query: body => ({
        url: '/credits/credit_programs/calculate-credit/',
        method: 'post',
        body
      })
    }),
    getUnsignedDocuments: build.query<DocumentTypeArray, GetUnsignedDocumentsPayload | null>({
      query: args => {
        if (Array.isArray(args)) {
          return {
            url: '/credits/unsigned_documents/',
            method: 'get',
            params: {
              category: args
            }
          }
        }
        return {
          url: '/credits/unsigned_documents/',
          method: 'get'
        }
      }
    }),
    addUnsignedDocument: build.mutation<AddUnsignedDocumentsResponse, AddUnsignedDocumentsPayload>({
      query: body => ({
        url: '/credits/unsigned_documents/',
        method: 'post',
        body
      })
    }),
    cancelCredit: build.mutation<CancelCreditResponse, string>({
      query: id => {
        return {
          url: `/credits/${id}/cancel/`,
          method: 'post'
        }
      }
    }),
    getBankTransferDetails: build.query<GetBankTransferDetailsResponse, GetBankTransferDetailsPayload>({
      query: id => ({
        url: `/credits/${id}/bank-details-transfer/`,
        method: 'get'
      })
    }),
    setCardDeliveryAddress: build.mutation<SetCardDeliveryAddressResponse, SetCardDeliveryAddressPayload>({
      query: body => ({
        url: '/credits/delivery/',
        method: 'post',
        body
      })
    }),
    getAnalytics: build.query<GetAnalyticsResponse, void | null>({
      query: () => ({
        url: '/credits/analytics/'
      })
    }),
    getBankManagers: build.query<PaginatedGetBankManagersResponse, Partial<GetBankManagersPayload>>({
      query: params => ({
        url: '/bank-manager/',
        method: 'get',
        params
      })
    }),
    attachBankManager: build.mutation<AttachBankManagerResponse, AttachBankManagerPayload>({
      query: ({ credit_id, body }) => ({
        url: `/credits/${credit_id}/attach-bank-manager/`,
        method: 'put',
        body
      })
    }),
    obtainCreditBankManager: build.query<ObtainCreditBankManagerResponse, ObtainCreditBankManagerPayload>({
      query: credit_id => ({
        url: `/credits/${credit_id}/obtain-bank-manager/`,
        method: 'get'
      })
    }),
    q19Call: build.mutation<Q19CallResponse, string>({
      query: step_uuid => ({
        url: `/telemarketing/q19-call/${step_uuid}`,
        method: 'post'
      })
    })
  })
})

export const {
  useGetCreditProgramsQuery,
  useGetCreditProgramQuery,
  useCalculateCreditMutation,
  useGetUnsignedDocumentsQuery,
  useLazyGetUnsignedDocumentsQuery,
  useAddUnsignedDocumentMutation,
  useCancelCreditMutation,
  useGetBankTransferDetailsQuery,
  useSetCardDeliveryAddressMutation,
  useGetAnalyticsQuery,
  useGetBankManagersQuery,
  useLazyGetBankManagersQuery,
  useAttachBankManagerMutation,
  useObtainCreditBankManagerQuery,
  useQ19CallMutation
} = authApi
